export type ByteUnit = 'B' | 'KB' | 'MB' | 'GB' | 'TB';

export const byteUnitOptions: Array<{
  value: ByteUnit;
  label: string;
  multiplier: number;
}> = [
  { value: 'B', label: 'Bytes', multiplier: 1 },
  { value: 'KB', label: 'KB', multiplier: 1024 },
  { value: 'MB', label: 'MB', multiplier: 1024 * 1024 },
  { value: 'GB', label: 'GB', multiplier: 1024 * 1024 * 1024 },
  { value: 'TB', label: 'TB', multiplier: 1024 * 1024 * 1024 * 1024 },
];

const byteUnitOptionsDesc = [...byteUnitOptions].reverse();

export const byteUnitMap = new Map(byteUnitOptions.map((u) => [u.value, u]));

export function isByteUnit(value: unknown): value is ByteUnit {
  return byteUnitOptions.some((unit) => unit.value === value);
}

export function convertBytesToHumanReadable(bytes: number): {
  value: number;
  unit: ByteUnit;
} {
  if (bytes === 0) return { value: 0, unit: 'B' };
  const unitsDesc = byteUnitOptionsDesc;
  // Skip 'B' — every integer byte count is exactly N bytes, so it would always
  // match and block the firstAboveOne fallback (e.g. 107374182 B would never
  // be shown as 102.4 MB).
  const exactMatch = unitsDesc.find((u) => {
    if (u.value === 'B') return false;
    const converted = bytes / u.multiplier;
    return converted >= 1 && Number.isInteger(converted);
  });
  if (exactMatch) {
    return {
      value: bytes / exactMatch.multiplier,
      unit: exactMatch.value,
    };
  }
  const firstAboveOne = unitsDesc.find((u) => bytes / u.multiplier >= 1);
  if (firstAboveOne) {
    const converted = bytes / firstAboveOne.multiplier;
    return {
      value: Math.round(converted * 100) / 100,
      unit: firstAboveOne.value,
    };
  }
  return { value: bytes, unit: 'B' };
}

export function formatDisplayValue(value: number): string {
  if (Number.isInteger(value)) {
    return value.toString();
  }
  // Very small values lose all precision with 2 decimal places (e.g. 2000 B
  // displayed in GB rounds to 0). Use 4 significant figures instead.
  if (Math.abs(value) < 0.01) {
    return parseFloat(value.toPrecision(4)).toString();
  }
  return (Math.round(value * 100) / 100).toString();
}
